import { AlertTriangle } from '@untitledui/icons';
import { type ErrorInfo, type ReactNode, Suspense, useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { ErrorBoundary } from '@/lib/base/error-boundary';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { extractChunkUrl, isChunkLoadError } from '@/lib/errors/chunkLoadError';
import { logger } from '@/lib/logger';

export interface LazyRouteBoundaryProps {
  children: ReactNode;
  /** Shown while lazy children resolve. Defaults to null. */
  suspenseFallback?: ReactNode;
  /** Surface label included in warn logs. */
  name?: string;
  /**
   * Contain non-chunk render errors inside this boundary (base ErrorBoundary
   * default UI, logged via logger.error with the component stack) instead of
   * rethrowing them.
   *
   * Default false: this boundary only handles chunk-load failures. Real
   * render bugs are rethrown so they reach the route errorElement
   * (ErrorPage), which owns crash reporting — the view-health error beacon
   * and the Report-a-problem flow. Opt in only for widgets that must not
   * take down their surrounding surface (e.g. a proposal card embedded in a
   * chat thread).
   */
  containRenderErrors?: boolean;
}

function ChunkReloadFallback() {
  return (
    <div
      data-testid='lazy-boundary-chunk-fallback'
      className='flex flex-col items-center justify-center gap-4 p-8 text-center'
    >
      <FeaturedIcon icon={AlertTriangle} size='lg' color='warning' />
      <div>
        <div className='text-primary font-medium'>New version available</div>
        <div className='text-secondary text-sm'>
          A new version of Ahoy is available. Reload to continue.
        </div>
      </div>
      <Button
        color='primary'
        size='md'
        onClick={() => window.location.reload()}
      >
        Reload
      </Button>
    </div>
  );
}

export function LazyRouteBoundary({
  children,
  suspenseFallback,
  name,
  containRenderErrors = false,
}: LazyRouteBoundaryProps) {
  const handleError = useCallback(
    (error: Error, info: ErrorInfo): void => {
      if (isChunkLoadError(error)) {
        logger.warn('LazyRouteBoundary: chunk-load error', {
          url: extractChunkUrl(error) ?? error.message,
          surface: name,
        });
        return;
      }
      if (containRenderErrors) {
        logger.error('LazyRouteBoundary: render error', error, {
          surface: name,
          componentStack: info.componentStack ?? undefined,
        });
      }
      // Non-contained render errors are rethrown to the route errorElement
      // (see renderFallback); ErrorPage owns their reporting, so logging them
      // here as well would double-count the crash.
    },
    [name, containRenderErrors]
  );

  const renderFallback = useCallback(
    (error: Error): ReactNode => {
      if (isChunkLoadError(error)) {
        return <ChunkReloadFallback />;
      }
      if (containRenderErrors) {
        // undefined → the base ErrorBoundary renders its own default error
        // UI, keeping that markup in a single place.
        return undefined;
      }
      // Real render bugs are not this boundary's concern: rethrow so the
      // route errorElement (ErrorPage) reports them (view-health error
      // beacon + Report-a-problem) exactly as on non-lazy routes.
      throw error;
    },
    [containRenderErrors]
  );

  return (
    <ErrorBoundary
      logErrors={false}
      onError={handleError}
      fallback={renderFallback}
    >
      <Suspense fallback={suspenseFallback ?? null}>{children}</Suspense>
    </ErrorBoundary>
  );
}
